import { recoverFromChunkLoadError } from '@/utils/chunk-reload-recovery';
import { logger } from '@/utils/logger/logger';
import {
  type FatalBoundaryName,
  reportBoundaryError,
} from '@/utils/telemetry/boundary-error';
import dynamic from 'next/dynamic';
import React, { type ComponentProps, type ComponentType, type ErrorInfo } from 'react';
import type ErrorComponentType from './errorComponent';

/** When dynamic importing, we lose LSP support to goto references from imported component. Workaround is to import the type */
const ErrorComponent: ComponentType<ComponentProps<typeof ErrorComponentType>> = dynamic(
  () => import('./errorComponent')
);

interface ErrorBoundaryProps {
  children: React.ReactNode;
  /** Intl-free fallback for boundaries mounted above `HeidiIntlProvider`. */
  fallback?: ComponentType;
  /** RUM `error_boundary.name` for this mount. */
  name?: FatalBoundaryName;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);

    // Define a state variable to track whether is an error or not
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error: null, errorInfo: null };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // You can use your own error logging service here
    logger.debug('ErrorBoundary caught an error');
    logger.debug({ error, errorInfo });
    this.setState({ error, errorInfo });

    // Every lazy boundary in the app that has no local containment lands here,
    // so this is where a tab holding chunk hashes from a rotated deployment
    // finally surfaces — as a full-page error whose own fallback is behind a
    // dynamic import that may be just as stale. Re-fetching the document is the
    // fix, and it costs nothing here: the tree this boundary guarded has already
    // been torn down. Reported to Datadog before the reload, and capped at one
    // reload per tab by the helper.
    const report = (recoveredByReload: boolean) =>
      reportBoundaryError(error, {
        boundary: this.props.name ?? 'app_root',
        fatal: true,
        recoveredByReload,
      });
    if (!recoverFromChunkLoadError(error, 'app-error-boundary', () => report(true))) {
      report(false);
    }
  }

  render() {
    // Check if the error is thrown
    if (this.state.hasError) {
      const Fallback = this.props.fallback ?? ErrorComponent;
      return <Fallback />;
    }

    // Return children components in case of no error
    return this.props.children;
  }
}

export default ErrorBoundary;
